import { Link } from "@tanstack/react-router";
import { ArrowUpRight, CalendarClock, Download, FlaskConical, GitBranch, RefreshCw, ScrollText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ResearchReportDetail, ResearchReportSchedule } from "@roboscientist/contracts";
import { OverflowReveal, StatusPill } from "@roboscientist/ui";
import { api } from "../api.js";
import { formatReportTimestamp, reportKindLabel } from "./reportPresentation.js";
import { ReportScheduleControls } from "./ReportScheduleControls.js";
import "../report.css";

const isInternalHref = (href: string): boolean =>
  href.startsWith("#") || (href.startsWith("/") && !href.startsWith("//"));

export function ResearchReportReader({
  report,
  schedule = null,
  embedded = false,
  onSelectExperiment,
  onSelectRun,
  onSummaryRequested,
  readOnly = false,
  downloadHref
}: {
  report: ResearchReportDetail;
  schedule?: ResearchReportSchedule | null;
  embedded?: boolean;
  onSelectExperiment?: (experimentId: string) => void;
  onSelectRun?: (runId: string) => void;
  onSummaryRequested?: () => void;
  readOnly?: boolean;
  downloadHref?: string | null;
}) {
  const effectiveSummaryIntervalMinutes = schedule?.updateIntervalMinutes
    ?? report.updateIntervalMinutes
    ?? (report.schemaVersion === 2 ? 60 : null);
  const effectiveNextUpdateDueAt = schedule?.nextUpdateDueAt ?? report.nextUpdateDueAt;

  const effectiveDownloadHref = downloadHref === undefined ? api.researchReportDownloadUrl(report.reportId) : downloadHref;
  return <main className={`report-reader${embedded ? " report-reader--embedded" : ""}`}>
    <header className="report-reader__heading">
      <div className="report-reader__badges">
        <StatusPill tone={report.kind === "final" ? "accent" : "live"}>{reportKindLabel(report.kind)}</StatusPill>
        <span>{report.version ? `Version ${report.version}` : "Published snapshot"}</span>
        <div className="report-reader__actions">
          {!readOnly && report.schemaVersion === 2 && report.rootThreadId ? <ReportScheduleControls
            rootThreadId={report.rootThreadId}
            schedule={schedule}
            fallbackIntervalMinutes={effectiveSummaryIntervalMinutes ?? 60}
            finalSnapshot={report.kind === "final"}
            onChanged={onSummaryRequested}
          /> : null}
          {effectiveDownloadHref ? <a className="report-download" href={effectiveDownloadHref} download={`${report.reportId}.md`}><Download size={13} /> Download report</a> : null}
        </div>
      </div>
      <h1>{report.title}</h1>
      {report.summary ? <p>{report.summary}</p> : null}
    </header>

    <section className="report-metadata" aria-label="Report metadata">
      <div><CalendarClock size={15} /><span><small>Coverage through</small><strong>{formatReportTimestamp(report.coverageThrough)}</strong></span></div>
      <div><ScrollText size={15} /><span><small>Published</small><strong>{formatReportTimestamp(report.publishedAt)}</strong></span></div>
      <div><GitBranch size={15} /><span><small>Author thread</small><OverflowReveal as="code" value={report.authorThreadId ?? "Unassigned"} kind="identifier" label="Author thread ID" copyable={Boolean(report.authorThreadId)} focusable /></span></div>
      {effectiveSummaryIntervalMinutes ? <div><RefreshCw size={15} /><span><small>Summary cadence</small><strong>Every {effectiveSummaryIntervalMinutes} minutes</strong></span></div> : null}
      {effectiveNextUpdateDueAt ? <div><CalendarClock size={15} /><span><small>Next update due</small><strong>{formatReportTimestamp(effectiveNextUpdateDueAt)}</strong></span></div> : null}
    </section>
    {report.experimentIds.length || report.runIds.length ? <section className="report-evidence" aria-label="Referenced evidence">
      <header><span className="panel__eyebrow">EVIDENCE REFERENCES</span><strong>{report.experimentIds.length} experiments · {report.runIds.length} runs</strong></header>
      <div>
        {report.experimentIds.map((experimentId) => onSelectExperiment
          ? <button key={`experiment:${experimentId}`} type="button" onClick={() => onSelectExperiment(experimentId)} aria-label={`Open experiment ${experimentId}`}><FlaskConical size={13} /> Experiment <OverflowReveal as="code" value={experimentId} kind="identifier" label="Experiment ID" copyable /></button>
          : <Link key={`experiment:${experimentId}`} to="/experiments/$experimentId" params={{ experimentId }} aria-label={`Open experiment ${experimentId}`}><FlaskConical size={13} /> Experiment <OverflowReveal as="code" value={experimentId} kind="identifier" label="Experiment ID" copyable /></Link>)}
        {report.runIds.map((runId) => onSelectRun
          ? <button key={`run:${runId}`} type="button" onClick={() => onSelectRun(runId)} aria-label={`Open run ${runId}`}><ScrollText size={13} /> Run <OverflowReveal as="code" value={runId} kind="identifier" label="Run ID" copyable /></button>
          : <Link key={`run:${runId}`} to="/runs/$runId" params={{ runId }} aria-label={`Open run ${runId}`}><ScrollText size={13} /> Run <OverflowReveal as="code" value={runId} kind="identifier" label="Run ID" copyable /></Link>)}
      </div>
    </section> : null}

    <article className="report-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ href, children }) => {
            if (!href) return <span>{children}</span>;
            const internal = isInternalHref(href);
            return <a href={href} target={internal ? undefined : "_blank"} rel={internal ? undefined : "noopener noreferrer"}>{children}{internal ? null : <ArrowUpRight size={12} aria-hidden="true" />}</a>;
          }
        }}
      >{report.markdown || "_This report has no rendered document body._"}</ReactMarkdown>
    </article>
    <footer className="report-document-facts">
      <span>Report document</span>
      <OverflowReveal as="code" value={report.document.path} kind="path" label="Report document path" copyable focusable />
    </footer>
  </main>;
}
